/* eslint-disable react-hooks/exhaustive-deps */
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import api from '../api/api';
import { getDateRangeBody, getParam, getTagsBody } from '../utils/communication';
import { Workspace } from '../models/common-types';
import constants, { LOADER, SECTION } from '../constants/constants';
import { FiltersHandlers, FiltersPanelHandlers, OutputFilters } from '../components/FiltersPanel/filter-types';
import { Report } from '../../investigate/Content/routes/report/models/Report';
import { i18n } from '../i18n/i18n';
import { DateRange } from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment';
import { NOTIFICATION, useNotifications } from '@cellebrite/saas-ui-common';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { pendoTrack } from '../utils/pendo';
import { dateRangeFormatter, formatPhoneNumber, numberPrettier } from '../utils/formatters';
import { addressesMap } from '../../investigate/Content/routes/collections/Collection/constants';
import { getIndexesOf } from '../utils/find';
import { scroller } from 'react-scroll';
import { getKeywordsFromWatchLists } from '../utils/utils';

export interface SectionSort {
    property: string;
    direction: string;
}

export default function useReport(activeWorkspace: Workspace | null, activeCaseId: string | null) {
    const getInitialStatuses = () => ({
        media: LOADER.STATUS.LOADING,
        posts: LOADER.STATUS.LOADING,
        insights: LOADER.STATUS.LOADING,
        locations: LOADER.STATUS.LOADING,
        connections: LOADER.STATUS.LOADING,
        webMentions: LOADER.STATUS.LOADING,
        personalInfo: LOADER.STATUS.LOADING,
        socialPresence: LOADER.STATUS.LOADING,
        summary: LOADER.STATUS.LOADING,
    });
    const getInitialExportFilters = () => {
        return {
            mediaBody: {
                sortBy: {
                    property: 'DATE',
                    direction: 'DESC',
                },
            },
            postsBody: {
                sortBy: {
                    property: 'DATE',
                    direction: 'DESC',
                },
            },
            connectionsBody: {
                sortBy: {
                    property: 'CONNECTION_STRENGTH',
                    direction: 'DESC',
                },
            },
        };
    };
    const queryClient = useQueryClient();
    const [showMessage] = useNotifications();
    const [report, setReport] = useState(new Report());
    const [streamId, setStreamId] = useState(null);
    const [status, setStatus] = useState(getInitialStatuses());
    const [connectionsFiltersPanelRef, setConnectionsFiltersPanelRef] =
        useState<MutableRefObject<FiltersPanelHandlers>>(null);
    const [dateRange, setDateRange] = useState<DateRange<Moment>>([null, null]);
    const [tags, setTags] = useState<string>('');
    const [exportFilters, setExportFilters]: any = useState(getInitialExportFilters());

    const [mediaSort, setMediaSort] = useState(null);
    const [mediaDirection, setMediaDirection] = useState(null);
    const [mediaOutputFilters, setMediaOutputFilters] = useState<OutputFilters | null>(null);
    const [connectionsSort, setConnectionsSort] = useState(null);
    const [connectionsDirection, setConnectionsDirection] = useState(null);
    const [connectionsOutputFilters, setConnectionsOutputFilters] = useState<OutputFilters | null>(null);
    const [postsSort, setPostsSort] = useState(null);
    const [postsDirection, setPostsDirection] = useState(null);
    const [postsOutputFilters, setPostsOutputFilters] = useState<OutputFilters | null>(null);
    const [find, setFind] = useState('');
    const displayHighlightsRef = useRef(false);
    const [displayHighlights, setDisplayHighlights] = useState(false);
    const [allHighlights, setAllHighlights] = useState(false);
    const [currentFindLink, setCurrentFindLink] = useState(null);
    const [currentHighlightsLink, setCurrentHighlightsLink] = useState(null);
    const [currentHighlightsWatchlist, setCurrentHighlightsWatchlist] = useState(null);
    const isReportPage = window.location.href.includes('report');
    const isQueriesEnabled = isReportPage && !!activeWorkspace?.id && !!activeCaseId && !!streamId;
    const highlightsLinks = useMemo(() => {
        const defaultOffset = -250;
        const findHighlightsArr = [];
        if (displayHighlights && currentHighlightsWatchlist) {
            report?.personalInformation?.names?.forEach((name) => {
                if (name?.value) {
                    getIndexesOf(
                        getKeywordsFromWatchLists(name.watchlist, currentHighlightsWatchlist?.id),
                        name.value,
                    ).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: name?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });

            report?.personalInformation?.usernames?.forEach((username) => {
                if (username?.value?.userName) {
                    getIndexesOf(
                        getKeywordsFromWatchLists(username.watchlist, currentHighlightsWatchlist?.id),
                        username.value.userName,
                    ).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: username.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });

            report?.personalInformation?.phones?.forEach((phone) => {
                if (formatPhoneNumber(phone?.value)) {
                    getIndexesOf(
                        getKeywordsFromWatchLists(phone.watchlist, currentHighlightsWatchlist?.id),
                        formatPhoneNumber(phone.value),
                    ).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: phone?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            report?.personalInformation?.emails?.forEach((email) => {
                if (email?.value) {
                    getIndexesOf(
                        getKeywordsFromWatchLists(email.watchlist, currentHighlightsWatchlist?.id),
                        email.value,
                    ).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: email?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            report?.personalInformation?.work?.jobs?.forEach((job) => {
                const jobKeywords = getKeywordsFromWatchLists(job.watchlist, currentHighlightsWatchlist?.id);
                if (job?.value?.jobTitle) {
                    getIndexesOf(jobKeywords, job.value.jobTitle).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: job?.identifier,
                            type: 'value',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
                if (job?.value?.company) {
                    getIndexesOf(jobKeywords, job.value.company).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: job?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset - 150,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
                const dateRange = dateRangeFormatter(job.value.start, job.value.end);
                if (dateRange) {
                    getIndexesOf(jobKeywords, dateRange).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: job?.identifier,
                            type: 'secondaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            report?.personalInformation?.education?.forEach((educationItem) => {
                const educationKeywords = getKeywordsFromWatchLists(
                    educationItem.watchlist,
                    currentHighlightsWatchlist?.id,
                );
                if (educationItem?.value?.degree) {
                    getIndexesOf(educationKeywords, educationItem.value.degree).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: educationItem?.identifier,
                            type: 'value',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
                if (educationItem?.value?.school) {
                    getIndexesOf(educationKeywords, educationItem.value.school).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: educationItem?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
                const dateRange = dateRangeFormatter(educationItem.value.start, educationItem.value.end);
                if (dateRange) {
                    getIndexesOf(educationKeywords, dateRange).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: educationItem?.identifier,
                            type: 'secondaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            report?.personalInformation?.locations?.forEach((location) => {
                const locationKeywords = getKeywordsFromWatchLists(location.watchlist, currentHighlightsWatchlist?.id);
                if (location?.value?.address) {
                    getIndexesOf(locationKeywords, location.value.address).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: location?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
                const label = addressesMap.get(location.value?.label?.toUpperCase());
                if (label) {
                    getIndexesOf(locationKeywords, label).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: location?.identifier,
                            type: 'secondaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            report?.personalInformation?.relationships?.forEach((relationship) => {
                const relationshipKeywords = getKeywordsFromWatchLists(
                    relationship.watchlist,
                    currentHighlightsWatchlist?.id,
                );
                if (relationship?.value?.value) {
                    getIndexesOf(relationshipKeywords, relationship.value.value).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: relationship?.identifier,
                            type: 'value',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
                if (relationship?.value?.type) {
                    getIndexesOf(relationshipKeywords, relationship?.value.type).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: relationship?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            report?.personalInformation?.userIds?.forEach((userId) => {
                const userIdKeywords = getKeywordsFromWatchLists(userId.watchlist, currentHighlightsWatchlist?.id);
                if (userId?.value?.userId) {
                    getIndexesOf(userIdKeywords, userId.value.userId).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: userId?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            report?.personalInformation?.urls?.forEach((url) => {
                const urlKeywords = getKeywordsFromWatchLists(url.watchlist, currentHighlightsWatchlist?.id);
                if (url?.value?.url) {
                    getIndexesOf(urlKeywords, url.value.url).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: url?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
                if (url?.value?.type) {
                    getIndexesOf(urlKeywords, url?.value.type).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: url?.identifier,
                            type: 'secondaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            report?.personalInformation?.vehicles?.forEach((vehicle) => {
                const vehicleKeywords = getKeywordsFromWatchLists(vehicle.watchlist, currentHighlightsWatchlist?.id);
                if (vehicle?.value?.vin) {
                    getIndexesOf(vehicleKeywords, vehicle.value.vin).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: vehicle?.identifier,
                            type: 'primaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
                if (vehicle?.value?.display) {
                    getIndexesOf(vehicleKeywords, vehicle?.value.display).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: vehicle?.identifier,
                            type: 'secondaryInfoText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.PERSONAL_INFO,
                        }),
                    );
                }
            });
            if (report?.personalInformation?.gender?.value) {
                const genderKeywords = getKeywordsFromWatchLists(
                    report.personalInformation.gender.watchlist,
                    currentHighlightsWatchlist?.id,
                );
                getIndexesOf(genderKeywords, report.personalInformation.gender.value).forEach((indexOf, index) =>
                    findHighlightsArr.push({
                        id: report?.personalInformation?.gender?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }

            report?.socialPresenceAccounts?.forEach((account) => {
                const accountKeywords = getKeywordsFromWatchLists(account.watchlist, currentHighlightsWatchlist?.id);
                if (account?.name) {
                    getIndexesOf(accountKeywords, account.name).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: account.identifier,
                            type: 'name',
                            index,
                            offset: defaultOffset,
                            section: SECTION.SOCIAL_PRESENCE,
                        }),
                    );
                }
                if (account?.userName) {
                    getIndexesOf(accountKeywords, account.userName).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: account.identifier,
                            type: 'userName',
                            index,
                            offset: defaultOffset,
                            section: SECTION.SOCIAL_PRESENCE,
                        }),
                    );
                }
                if (account?.userId) {
                    getIndexesOf(accountKeywords, account.userId).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: account.identifier,
                            type: 'userId',
                            index,
                            offset: defaultOffset,
                            section: SECTION.SOCIAL_PRESENCE,
                        }),
                    );
                }
                if (account.aboutText || account.profileLocation || account.website) {
                    getIndexesOf(accountKeywords, account.aboutText).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: account.identifier,
                            type: 'aboutValue',
                            index,
                            offset: defaultOffset,
                            section: SECTION.SOCIAL_PRESENCE,
                        }),
                    );
                    getIndexesOf(accountKeywords, account.profileLocation).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: account.identifier,
                            type: 'locationValue',
                            index,
                            offset: defaultOffset,
                            section: SECTION.SOCIAL_PRESENCE,
                        }),
                    );
                    getIndexesOf(accountKeywords, account.website).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: account.identifier,
                            type: 'website',
                            index,
                            offset: defaultOffset,
                            section: SECTION.SOCIAL_PRESENCE,
                        }),
                    );
                }
            });

            report?.connections?.connections.forEach((connection) => {
                const connectionKeywords = getKeywordsFromWatchLists(
                    connection.watchlist,
                    currentHighlightsWatchlist?.id,
                );
                if (connection?.name) {
                    getIndexesOf(connectionKeywords, connection.name).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: connection.identifier,
                            type: 'name',
                            index,
                            offset: defaultOffset,
                            section: SECTION.CONNECTIONS,
                        }),
                    );
                }
                if (connection?.userName) {
                    getIndexesOf(connectionKeywords, connection.userName).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: connection.identifier,
                            type: 'userId',
                            index,
                            offset: defaultOffset,
                            section: SECTION.CONNECTIONS,
                        }),
                    );
                }
                if (connection?.userId) {
                    getIndexesOf(connectionKeywords, connection.userId).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: connection.identifier,
                            type: 'userId',
                            index,
                            offset: defaultOffset,
                            section: SECTION.CONNECTIONS,
                        }),
                    );
                }
                if (connection?.aboutText) {
                    getIndexesOf(connectionKeywords, connection.aboutText).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: connection.identifier,
                            type: 'aboutText',
                            index,
                            offset: defaultOffset,
                            section: SECTION.CONNECTIONS,
                        }),
                    );
                }
            });

            _.orderBy(report?.locations, (location) => moment(location.startDate).toDate(), ['desc'])?.forEach(
                (location) => {
                    const locationKeywords = getKeywordsFromWatchLists(
                        location.watchlist,
                        currentHighlightsWatchlist?.id,
                    );
                    const locationOffset = defaultOffset - 50;
                    const address =
                        !location?.addressDetails?.fullAddress ||
                        location?.addressDetails?.fullAddress === location?.address
                            ? location?.address
                            : `${location?.addressDetails?.fullAddress} (${location?.address})`;
                    if (address) {
                        getIndexesOf(locationKeywords, address).forEach((indexOf, index) => {
                            findHighlightsArr.push({
                                id: location.identifier,
                                type: 'address',
                                index,
                                offset: locationOffset,
                                section: SECTION.LOCATIONS,
                            });
                        });
                    }
                    if (location?.type) {
                        getIndexesOf(locationKeywords, location.type).forEach((indexOf, index) =>
                            findHighlightsArr.push({
                                id: location.identifier,
                                type: 'type',
                                index,
                                offset: locationOffset,
                                section: SECTION.LOCATIONS,
                            }),
                        );
                    }
                    if (location?.label) {
                        getIndexesOf(locationKeywords, location.label).forEach((indexOf, index) =>
                            findHighlightsArr.push({
                                id: location.identifier,
                                type: 'label',
                                index,
                                offset: locationOffset,
                                section: SECTION.LOCATIONS,
                            }),
                        );
                    }
                },
            );

            report?.posts?.posts?.postsMappedByDate?.forEach((post) => {
                const postKeywords = getKeywordsFromWatchLists(post.watchlist, currentHighlightsWatchlist?.id);
                if (post?.profileInfo?.name) {
                    getIndexesOf(postKeywords, post.profileInfo.name).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: post.contentId,
                            type: 'name',
                            index,
                            offset: defaultOffset,
                            section: SECTION.POSTS,
                        }),
                    );
                }
                if (post?.profileInfo?.id) {
                    getIndexesOf(postKeywords, post.profileInfo.id).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: post.contentId,
                            type: 'id',
                            index,
                            offset: defaultOffset,
                            section: SECTION.POSTS,
                        }),
                    );
                }
                if (post?.bodyTitle) {
                    getIndexesOf(postKeywords, post.bodyTitle).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: post.contentId,
                            type: 'bodyTitle',
                            index,
                            offset: defaultOffset,
                            section: SECTION.POSTS,
                        }),
                    );
                }
                if (post?.body) {
                    getIndexesOf(postKeywords, post.body).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: post.contentId,
                            type: 'body',
                            index,
                            offset: defaultOffset,
                            section: SECTION.POSTS,
                        }),
                    );
                }
                if (post.quotedPosts?.length) {
                    const quotedPost = post.quotedPosts?.[0];
                    if (quotedPost?.author?.name) {
                        getIndexesOf(postKeywords, quotedPost.author.name).forEach((indexOf, index) =>
                            findHighlightsArr.push({
                                id: quotedPost.contentId,
                                type: 'quotedPostName',
                                index,
                                offset: defaultOffset,
                                section: SECTION.POSTS,
                            }),
                        );
                    }
                    if (quotedPost?.author?.username) {
                        getIndexesOf(postKeywords, quotedPost.author.username).forEach((indexOf, index) =>
                            findHighlightsArr.push({
                                id: quotedPost.contentId,
                                type: 'quotedPostUsername',
                                index,
                                offset: defaultOffset,
                                section: SECTION.POSTS,
                            }),
                        );
                    }
                    if (quotedPost?.body) {
                        getIndexesOf(postKeywords, quotedPost.body).forEach((indexOf, index) =>
                            findHighlightsArr.push({
                                id: quotedPost.contentId,
                                type: 'quotedPostBody',
                                index,
                                offset: defaultOffset,
                                section: SECTION.POSTS,
                            }),
                        );
                    }
                }
                if (
                    post?.watchlistReactions.filter(
                        (watchList) =>
                            watchList.hasWatchlistInReactions && watchList.watchlistId == currentHighlightsWatchlist.id,
                    ).length
                ) {
                    findHighlightsArr.push({
                        id: post.contentId,
                        type: 'reactions',
                        index: 0,
                        offset: defaultOffset,
                        section: SECTION.POSTS,
                    });
                }
                if (
                    post?.watchlistComments?.filter(
                        (watchList) =>
                            watchList.hasWatchlistInComments && watchList.watchlistId == currentHighlightsWatchlist.id,
                    ).length
                ) {
                    findHighlightsArr.push({
                        id: post.contentId,
                        type: 'comments',
                        index: 0,
                        offset: defaultOffset,
                        section: SECTION.POSTS,
                    });
                }
            });

            report?.webMentions?.forEach((webMention) => {
                const webMentionKeywords = getKeywordsFromWatchLists(
                    webMention?.watchlist,
                    currentHighlightsWatchlist?.id,
                );
                if (webMention?.url) {
                    getIndexesOf(webMentionKeywords, webMention.url).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: webMention.identifier,
                            type: 'url',
                            index,
                            offset: defaultOffset,
                            section: SECTION.WEB_MENTIONS,
                        }),
                    );
                }
                if (webMention?.searchResultTitle) {
                    getIndexesOf(webMentionKeywords, webMention.searchResultTitle).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: webMention.identifier,
                            type: 'searchResultTitle',
                            index,
                            offset: defaultOffset,
                            section: SECTION.WEB_MENTIONS,
                        }),
                    );
                }
                if (webMention?.searchResultSnippet) {
                    getIndexesOf(webMentionKeywords, webMention.searchResultSnippet).forEach((indexOf, index) =>
                        findHighlightsArr.push({
                            id: webMention.identifier,
                            type: 'searchResultSnippet',
                            index,
                            offset: defaultOffset,
                            section: SECTION.WEB_MENTIONS,
                        }),
                    );
                }
            });
        }
        const currentLink = findHighlightsArr?.[0];
        if (currentLink) {
            setCurrentHighlightsLink(currentLink);
            setTimeout(
                () =>
                    scroller.scrollTo(currentLink.id + currentLink.section, {
                        offset: currentLink.offset,
                        spy: true,
                        smooth: true,
                        duration: 500,
                    }),
                300,
            );
        }
        return findHighlightsArr;
    }, [displayHighlights, currentHighlightsWatchlist]);
    const findLinks = useMemo(() => {
        const findLinksArr = [];
        const defaultOffset = -270;
        report?.personalInformation?.names?.forEach((name) => {
            if (name?.value) {
                getIndexesOf(find, name.value).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: name?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.usernames?.forEach((username) => {
            if (username?.value?.userName) {
                getIndexesOf(find, username.value.userName).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: username?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.phones?.forEach((phone) => {
            if (formatPhoneNumber(phone?.value)) {
                getIndexesOf(find, formatPhoneNumber(phone.value)).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: phone?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.emails?.forEach((email) => {
            if (email?.value) {
                getIndexesOf(find, email.value).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: email?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.work?.jobs?.forEach((job) => {
            if (job?.value?.jobTitle) {
                getIndexesOf(find, job.value.jobTitle).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: job?.identifier,
                        type: 'value',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
            if (job?.value?.company) {
                getIndexesOf(find, job.value.company).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: job?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset - 150,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
            const dateRange = dateRangeFormatter(job.value.start, job.value.end);
            if (dateRange) {
                getIndexesOf(find, dateRange).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: job?.identifier,
                        type: 'secondaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.education?.forEach((educationItem) => {
            if (educationItem?.value?.degree) {
                getIndexesOf(find, educationItem.value.degree).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: educationItem?.identifier,
                        type: 'value',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
            if (educationItem?.value?.school) {
                getIndexesOf(find, educationItem.value.school).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: educationItem?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
            const dateRange = dateRangeFormatter(educationItem.value.start, educationItem.value.end);
            if (dateRange) {
                getIndexesOf(find, dateRange).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: educationItem?.identifier,
                        type: 'secondaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.locations?.forEach((location) => {
            if (location?.value?.address) {
                getIndexesOf(find, location.value.address).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: location?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
            const label = addressesMap.get(location.value?.label?.toUpperCase());
            if (label) {
                getIndexesOf(find, label).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: location?.identifier,
                        type: 'secondaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.relationships?.forEach((relationship) => {
            if (relationship?.value?.value) {
                getIndexesOf(find, relationship.value.value).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: relationship?.identifier,
                        type: 'value',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
            if (relationship?.value?.type) {
                getIndexesOf(find, relationship?.value.type).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: relationship?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.userIds?.forEach((userId) => {
            if (userId?.value?.userId) {
                getIndexesOf(find, userId.value.userId).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: userId?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.urls?.forEach((url) => {
            if (url?.value?.url) {
                getIndexesOf(find, url.value.url).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: url?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
            if (url?.value?.type) {
                getIndexesOf(find, url?.value.type).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: url?.identifier,
                        type: 'secondaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        report?.personalInformation?.vehicles?.forEach((vehicle) => {
            if (vehicle?.value?.vin) {
                getIndexesOf(find, vehicle.value.vin).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: vehicle?.identifier,
                        type: 'primaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
            if (vehicle?.value?.display) {
                getIndexesOf(find, vehicle?.value.display).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: vehicle?.identifier,
                        type: 'secondaryInfoText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.PERSONAL_INFO,
                    }),
                );
            }
        });
        if (report?.personalInformation?.gender?.value) {
            getIndexesOf(find, report.personalInformation.gender.value).forEach((indexOf, index) =>
                findLinksArr.push({
                    id: report?.personalInformation?.gender?.identifier,
                    type: 'primaryInfoText',
                    index,
                    offset: defaultOffset,
                    section: SECTION.PERSONAL_INFO,
                }),
            );
        }
        report?.socialPresenceAccounts?.forEach((account) => {
            if (account?.name) {
                getIndexesOf(find, account.name).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'name',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
            }
            if (account?.userName) {
                getIndexesOf(find, account.userName).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'userName',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
            }
            if (account?.userId) {
                getIndexesOf(find, account.userId).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'userId',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
            }
            if (
                account.aboutText ||
                account.profileLocation ||
                account.website ||
                account.counts?.following ||
                account.counts?.followers ||
                account.counts?.posts ||
                account.counts?.listed
            ) {
                getIndexesOf(find, 'about').forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'aboutKey',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, account.aboutText).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'aboutValue',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, 'location').forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'locationKey',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, account.profileLocation).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'locationValue',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, 'links').forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'linksKey',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, account.website).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'website',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, numberPrettier(account.counts?.following)).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'followingValue',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, 'following').forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'followingKey',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, numberPrettier(account.counts?.followers)).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'followersValue',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, 'followers').forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'followersKey',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, numberPrettier(account.counts?.posts)).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'postsValue',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, 'posts').forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'postsKey',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, numberPrettier(account.counts?.listed)).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'listedValue',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
                getIndexesOf(find, 'listed').forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: account.identifier,
                        type: 'listedKey',
                        index,
                        offset: defaultOffset,
                        section: SECTION.SOCIAL_PRESENCE,
                    }),
                );
            }
        });
        report?.connections?.socialAccounts?.forEach((socialAccount) => {
            if (socialAccount?.name) {
                getIndexesOf(find, socialAccount.name).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: socialAccount.id,
                        type: 'name',
                        index,
                        offset: defaultOffset,
                        section: SECTION.CONNECTIONS,
                    }),
                );
            }
            if (socialAccount?.id) {
                getIndexesOf(find, socialAccount.id).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: socialAccount.id,
                        type: 'id',
                        index,
                        offset: defaultOffset,
                        section: SECTION.CONNECTIONS,
                    }),
                );
            }
        });
        report?.connections?.connections.forEach((connection) => {
            if (connection?.name) {
                getIndexesOf(find, connection.name).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: connection.identifier,
                        type: 'name',
                        index,
                        offset: defaultOffset,
                        section: SECTION.CONNECTIONS,
                    }),
                );
            }
            if (connection?.userName) {
                getIndexesOf(find, connection.userName).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: connection.identifier,
                        type: 'userId',
                        index,
                        offset: defaultOffset,
                        section: SECTION.CONNECTIONS,
                    }),
                );
            }
            if (connection?.userId) {
                getIndexesOf(find, connection.userId).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: connection.identifier,
                        type: 'userId',
                        index,
                        offset: defaultOffset,
                        section: SECTION.CONNECTIONS,
                    }),
                );
            }
            if (connection?.aboutText) {
                getIndexesOf(find, connection.aboutText).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: connection.identifier,
                        type: 'aboutText',
                        index,
                        offset: defaultOffset,
                        section: SECTION.CONNECTIONS,
                    }),
                );
            }
        });
        _.orderBy(report?.locations, (location) => moment(location.startDate).toDate(), ['desc'])?.forEach(
            (location) => {
                const locationOffset = defaultOffset - 50;
                const address =
                    !location?.addressDetails?.fullAddress ||
                    location?.addressDetails?.fullAddress === location?.address
                        ? location?.address
                        : `${location?.addressDetails?.fullAddress} (${location?.address})`;
                if (address) {
                    getIndexesOf(find, address).forEach((indexOf, index) => {
                        findLinksArr.push({
                            id: location.identifier,
                            type: 'address',
                            index,
                            offset: locationOffset,
                            section: SECTION.LOCATIONS,
                        });
                    });
                }
                if (location?.type) {
                    getIndexesOf(find, location.type).forEach((indexOf, index) =>
                        findLinksArr.push({
                            id: location.identifier,
                            type: 'type',
                            index,
                            offset: locationOffset,
                            section: SECTION.LOCATIONS,
                        }),
                    );
                }
                if (location?.label) {
                    getIndexesOf(find, location.label).forEach((indexOf, index) =>
                        findLinksArr.push({
                            id: location.identifier,
                            type: 'label',
                            index,
                            offset: locationOffset,
                            section: SECTION.LOCATIONS,
                        }),
                    );
                }
            },
        );
        report?.posts?.posts?.postsMappedByDate?.forEach((post) => {
            if (post?.profileInfo?.name) {
                getIndexesOf(find, post.profileInfo.name).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: post.contentId,
                        type: 'name',
                        index,
                        offset: defaultOffset,
                        section: SECTION.POSTS,
                    }),
                );
            }
            if (post?.profileInfo?.id) {
                getIndexesOf(find, post.profileInfo.id).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: post.contentId,
                        type: 'id',
                        index,
                        offset: defaultOffset,
                        section: SECTION.POSTS,
                    }),
                );
            }
            if (post?.bodyTitle) {
                getIndexesOf(find, post.bodyTitle).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: post.contentId,
                        type: 'bodyTitle',
                        index,
                        offset: defaultOffset,
                        section: SECTION.POSTS,
                    }),
                );
            }
            if (post?.body) {
                getIndexesOf(find, post.body).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: post.contentId,
                        type: 'body',
                        index,
                        offset: defaultOffset,
                        section: SECTION.POSTS,
                    }),
                );
            }
            if (post.quotedPosts?.length) {
                const quotedPost = post.quotedPosts?.[0];
                if (quotedPost?.author?.name) {
                    getIndexesOf(find, quotedPost.author.name).forEach((indexOf, index) =>
                        findLinksArr.push({
                            id: quotedPost.contentId,
                            type: 'quotedPostName',
                            index,
                            offset: defaultOffset,
                            section: SECTION.POSTS,
                        }),
                    );
                }
                if (quotedPost?.author?.username) {
                    getIndexesOf(find, quotedPost.author.username).forEach((indexOf, index) =>
                        findLinksArr.push({
                            id: quotedPost.contentId,
                            type: 'quotedPostUsername',
                            index,
                            offset: defaultOffset,
                            section: SECTION.POSTS,
                        }),
                    );
                }
                if (quotedPost?.body) {
                    getIndexesOf(find, quotedPost.body).forEach((indexOf, index) =>
                        findLinksArr.push({
                            id: quotedPost.contentId,
                            type: 'quotedPostBody',
                            index,
                            offset: defaultOffset,
                            section: SECTION.POSTS,
                        }),
                    );
                }
            }
        });
        report?.webMentions?.forEach((webMention) => {
            if (webMention?.url) {
                getIndexesOf(find, webMention.url).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: webMention.identifier,
                        type: 'url',
                        index,
                        offset: defaultOffset,
                        section: SECTION.WEB_MENTIONS,
                    }),
                );
            }
            if (webMention?.searchResultTitle) {
                getIndexesOf(find, webMention.searchResultTitle).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: webMention.identifier,
                        type: 'searchResultTitle',
                        index,
                        offset: defaultOffset,
                        section: SECTION.WEB_MENTIONS,
                    }),
                );
            }
            if (webMention?.searchResultSnippet) {
                getIndexesOf(find, webMention.searchResultSnippet).forEach((indexOf, index) =>
                    findLinksArr.push({
                        id: webMention.identifier,
                        type: 'searchResultSnippet',
                        index,
                        offset: defaultOffset,
                        section: SECTION.WEB_MENTIONS,
                    }),
                );
            }
        });

        return findLinksArr;
    }, [report?.connections, report?.posts, find]);

    const initReport = () => {
        setReport(new Report());
    };
    const { data: collection } = useQuery(
        ['reportCollection', activeWorkspace?.id, activeCaseId, streamId],
        ({ queryKey }) =>
            api.collections.fetchCollectionById(queryKey[1] as number, queryKey[2] as string, queryKey[3] as string),
        {
            enabled: isQueriesEnabled,
            retry: false,
        },
    );

    const { data: moreResults } = useQuery(
        ['moreResults', activeWorkspace?.id, activeCaseId, streamId],
        ({ queryKey }) =>
            api.report.fetchMoreResults(queryKey[1] as number, queryKey[2] as string, queryKey[3] as string),
        {
            enabled: isQueriesEnabled,
            refetchInterval: constants.MORE_RESULTS_POLLING_DELAY,
            retry: false,
        },
    );

    useQuery(
        [SECTION.SUMMARY, activeWorkspace?.id, activeCaseId, streamId, dateRange, tags],
        ({ queryKey }) =>
            api.report.getSummarySection(
                queryKey[1] as number,
                queryKey[2] as string,
                queryKey[3] as string,
                queryKey[4],
                queryKey[5],
            ),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                report.updateSection(rawData);
                if (rawData) {
                    status[SECTION.SUMMARY] = LOADER.STATUS.LOADED;
                } else {
                    status[SECTION.SUMMARY] = LOADER.STATUS.FAILED;
                }
                setStatus(_.cloneDeep(status));
            },
            onError: (error) => {
                status[SECTION.SUMMARY] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    useQuery(
        [SECTION.PERSONAL_INFO, activeWorkspace?.id, activeCaseId, streamId, dateRange, tags],
        ({ queryKey }) =>
            api.report.getPersonalInformationSection(
                queryKey[1] as number,
                queryKey[2] as string,
                queryKey[3] as string,
                queryKey[4],
                queryKey[5],
            ),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                const overview = rawData?.overview;
                report.updateSection(rawData);
                if (
                    overview?.coverPictures?.length ||
                    overview?.dob?.length ||
                    overview?.education?.length ||
                    overview?.emails?.length ||
                    overview?.locations?.length ||
                    overview?.names?.length ||
                    overview?.phones?.length ||
                    overview?.profilePictures?.length ||
                    overview?.relationships?.length ||
                    overview?.urls?.length ||
                    overview?.userIds?.length ||
                    overview?.usernames?.length ||
                    overview?.gender
                ) {
                    status[SECTION.PERSONAL_INFO] = LOADER.STATUS.LOADED;
                } else {
                    status[SECTION.PERSONAL_INFO] = LOADER.STATUS.FAILED;
                }
                setStatus(_.cloneDeep(status));
            },
            onError: (error) => {
                status[SECTION.PERSONAL_INFO] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    useQuery(
        [SECTION.INSIGHTS, activeWorkspace?.id, activeCaseId, streamId, dateRange, tags],
        ({ queryKey }) =>
            api.report.getInsightsSection(
                queryKey[1] as number,
                queryKey[2] as string,
                queryKey[3] as string,
                queryKey[4],
                queryKey[5],
            ),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                if (rawData) {
                    rawData.sectionName = 'insightsSection';
                    report.updateSection(rawData);
                    status[SECTION.INSIGHTS] = LOADER.STATUS.LOADED;
                    setStatus(_.cloneDeep(status));
                }
            },
            onError: (error) => {
                status[SECTION.INSIGHTS] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    useQuery(
        [SECTION.SOCIAL_PRESENCE, activeWorkspace?.id, activeCaseId, streamId, tags],
        ({ queryKey }) =>
            api.report.getSocialPresenceSection(
                queryKey[1] as number,
                queryKey[2] as string,
                queryKey[3] as string,
                queryKey[4],
            ),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                report.updateSection(rawData);
                if (rawData?.socialAccounts?.length) {
                    status[SECTION.SOCIAL_PRESENCE] = LOADER.STATUS.LOADED;
                } else {
                    status[SECTION.SOCIAL_PRESENCE] = LOADER.STATUS.FAILED;
                }
                setStatus(_.cloneDeep(status));
            },
            onError: (error) => {
                status[SECTION.SOCIAL_PRESENCE] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    const { isLoading: isConnectionsLoading } = useQuery(
        [
            SECTION.CONNECTIONS,
            activeWorkspace?.id,
            activeCaseId,
            streamId,
            dateRange,
            tags,
            connectionsOutputFilters,
            connectionsSort,
            connectionsDirection,
        ],
        ({ queryKey }) =>
            api.report.getConnectionsSection(queryKey[1] as number, queryKey[2] as string, queryKey[3] as string, {
                ...{
                    ...queryKey[6],
                    sortBy: {
                        property: queryKey[7] || 'CONNECTION_STRENGTH',
                        direction: queryKey[8] || 'DESC',
                    },
                },
                ...getDateRangeBody(queryKey[4]),
                ...getTagsBody(queryKey[5]),
            }),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                report.updateSection(rawData);
                if (rawData?.connections?.length) {
                    status[SECTION.CONNECTIONS] = LOADER.STATUS.LOADED;
                } else {
                    status[SECTION.CONNECTIONS] = LOADER.STATUS.FAILED;
                }
                setStatus(_.cloneDeep(status));
            },
            onError: (error) => {
                status[SECTION.CONNECTIONS] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    const { isLoading: isMediaLoading } = useQuery(
        [
            SECTION.MEDIA,
            activeWorkspace?.id,
            activeCaseId,
            streamId,
            dateRange,
            tags,
            mediaOutputFilters,
            mediaSort,
            mediaDirection,
        ],
        ({ queryKey }) =>
            api.report.getMediaSection(queryKey[1] as number, queryKey[2] as string, queryKey[3] as string, {
                ...{
                    ...queryKey[6],
                    sortBy: {
                        property: queryKey[7] || 'DATE',
                        direction: queryKey[8] || 'DESC',
                    },
                },
                ...getDateRangeBody(queryKey[4]),
                ...getTagsBody(queryKey[5]),
            }),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                report.updateSection(rawData);
                if (rawData?.media?.length) {
                    status[SECTION.MEDIA] = LOADER.STATUS.LOADED;
                } else {
                    status[SECTION.MEDIA] = LOADER.STATUS.FAILED;
                }
                setStatus(_.cloneDeep(status));
            },
            onError: (error) => {
                status[SECTION.MEDIA] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    useQuery(
        [SECTION.LOCATIONS, activeWorkspace?.id, activeCaseId, streamId, dateRange, tags],
        ({ queryKey }) =>
            api.report.getLocationsSection(
                queryKey[1] as number,
                queryKey[2] as string,
                queryKey[3] as string,
                queryKey[4],
                queryKey[5],
            ),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                report.updateSection(rawData);
                if (rawData?.locations?.length) {
                    status[SECTION.LOCATIONS] = LOADER.STATUS.LOADED;
                } else {
                    status[SECTION.LOCATIONS] = LOADER.STATUS.FAILED;
                }
                setStatus(_.cloneDeep(status));
            },
            onError: (error) => {
                status[SECTION.LOCATIONS] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    const { isLoading: isPostsLoading } = useQuery(
        [
            SECTION.POSTS,
            activeWorkspace?.id,
            activeCaseId,
            streamId,
            dateRange,
            tags,
            postsOutputFilters,
            postsSort,
            postsDirection,
        ],
        ({ queryKey }) =>
            api.report.getPostsSection(queryKey[1] as number, queryKey[2] as string, queryKey[3] as string, {
                ...{
                    ...queryKey[6],
                    sortBy: {
                        property: queryKey[7] || 'DATE',
                        direction: queryKey[8] || 'DESC',
                    },
                },
                ...getDateRangeBody(queryKey[4]),
                ...getTagsBody(queryKey[5]),
            }),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                report.updateSection(rawData);
                if (rawData?.posts?.length) {
                    status[SECTION.POSTS] = LOADER.STATUS.LOADED;
                } else {
                    status[SECTION.POSTS] = LOADER.STATUS.FAILED;
                }
                setStatus(_.cloneDeep(status));
            },
            onError: (error) => {
                status[SECTION.POSTS] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    useQuery(
        [SECTION.WEB_MENTIONS, activeWorkspace?.id, activeCaseId, streamId, tags],
        ({ queryKey }) =>
            api.report.getWebMentionsSection(
                queryKey[1] as number,
                queryKey[2] as string,
                queryKey[3] as string,
                queryKey[4],
            ),
        {
            enabled: isQueriesEnabled,
            refetchInterval: collection?.status === 'Complete' ? false : constants.REPORT_POLLING_DELAY,
            retry: false,
            onSuccess: (rawData) => {
                report.updateSection(rawData);
                if (rawData?.webMentions?.length) {
                    status[SECTION.WEB_MENTIONS] = LOADER.STATUS.LOADED;
                } else {
                    status[SECTION.WEB_MENTIONS] = LOADER.STATUS.FAILED;
                }
                setStatus(_.cloneDeep(status));
            },
            onError: (error) => {
                status[SECTION.WEB_MENTIONS] = LOADER.STATUS.FAILED;
                setStatus(_.cloneDeep(status));
            },
        },
    );

    const fetchReport = (skipMedia?) => {
        queryClient.invalidateQueries(SECTION.SUMMARY);
        queryClient.invalidateQueries(SECTION.PERSONAL_INFO);
        queryClient.invalidateQueries(SECTION.INSIGHTS);
        queryClient.invalidateQueries(SECTION.SOCIAL_PRESENCE);
        queryClient.invalidateQueries(SECTION.CONNECTIONS);
        if (!skipMedia) {
            queryClient.invalidateQueries(SECTION.MEDIA);
        }
        queryClient.invalidateQueries(SECTION.LOCATIONS);
        queryClient.invalidateQueries(SECTION.POSTS);
        queryClient.invalidateQueries(SECTION.WEB_MENTIONS);
    };

    const exportPDF = async (options) => {
        if (dateRange) {
            options = { ...options, ...getDateRangeBody(dateRange) };
        }
        showMessage(i18n.translate('REPORT_PDF_GENERATION_START'), NOTIFICATION.TYPE.INFO);
        const res = await api.report.exportPDF(activeWorkspace.id, getParam('caseId'), streamId, options);
        return !(!res || res.data?.error) ? res : null;
    };

    const exportJSON = async () => {
        let options;
        if (dateRange) {
            options = {
                ...{
                    profileOverview: true,
                    socialPresence: true,
                    media: true,
                    personalInformation: true,
                    connections: true,
                    locations: true,
                    insights: true,
                    posts: true,
                    webMentions: true,
                    includeFilteredData: true,
                    extractImagesToZip: false,
                    reportType: 'JSON',
                    filters: {
                        mediaBody: {
                            sortBy: {
                                property: 'DATE',
                                direction: 'DESC',
                            },
                        },
                        postsBody: {
                            sortBy: {
                                property: 'DATE',
                                direction: 'DESC',
                            },
                        },
                        connectionsBody: {
                            sortBy: {
                                property: 'CONNECTION_STRENGTH',
                                direction: 'DESC',
                            },
                        },
                    },
                },
                ...getDateRangeBody(dateRange),
            };
        }
        showMessage(i18n.translate('REPORT_JSON_GENERATION_START'), NOTIFICATION.TYPE.INFO);
        const res = await api.report.exportJSON(activeWorkspace.id, getParam('caseId'), streamId, options);
        return !(!res || res.data?.error) ? res : null;
    };

    const initializeStatuses = () => {
        setStatus(_.cloneDeep(getInitialStatuses()));
    };

    const initializeFilters = () => {
        setMediaSort(null);
        setMediaDirection(null);
        setMediaOutputFilters(null);
        setConnectionsSort(null);
        setConnectionsDirection(null);
        setConnectionsOutputFilters(null);
        setPostsSort(null);
        setPostsDirection(null);
        setPostsOutputFilters(null);
        setExportFilters(_.cloneDeep(getInitialExportFilters()));
    };

    const getPostById = async (postId: string, site: string) =>
        await api.report.getPostById(activeWorkspace.id, getParam('caseId'), streamId, postId, site);

    useEffect(() => {
        if (find?.length > 2 && status[SECTION.MEDIA] !== LOADER.STATUS.DISABLED) {
            queryClient.cancelQueries({ queryKey: ['media'] });
            status[SECTION.MEDIA] = LOADER.STATUS.DISABLED;
            setStatus(_.cloneDeep(status));
        } else if (find?.length <= 2) {
            if (status[SECTION.MEDIA] === LOADER.STATUS.DISABLED) {
                status[SECTION.MEDIA] = LOADER.STATUS.LOADING;
                queryClient.invalidateQueries('media');
            }
            setStatus(_.cloneDeep(status));
        }
    }, [find]);

    const updateMoreResults = useMutation(
        (reqParams: {
            pendoEventName: string;
            data: { moreResultsIds: string[]; caseId: string; section: string };
            successCallback: () => void;
        }) => {
            pendoTrack(reqParams.pendoEventName, {
                moreResultsIds: reqParams.data.moreResultsIds,
                wsId: activeWorkspace?.id,
                caseId: reqParams.data.caseId,
                streamId,
            });
            return api.report.updateMoreResults(
                reqParams.data.moreResultsIds,
                activeWorkspace?.id,
                reqParams.data.caseId,
                streamId,
                reqParams.data.section,
            );
        },
        {
            onSuccess(_res, reqParams) {
                reqParams.successCallback();
                queryClient.invalidateQueries('moreResults');
            },
            onError(_res, reqParams) {
                queryClient.invalidateQueries('moreResults');
            },
        },
    );

    const refreshWatchLists = useMutation(
        (reqParams: { successCallback: () => void; errorCallback: () => void }) => {
            return api.report.refreshWatchLists(activeWorkspace?.id, activeCaseId, streamId);
        },
        {
            onSuccess(_res, reqParams) {
                queryClient.invalidateQueries('watchLists');
                fetchReport(true);
                showMessage(i18n.translate('Watchlists updated successfully.'), NOTIFICATION.TYPE.SUCCESS);
                reqParams.successCallback();
            },
            onError(_res, reqParams) {
                showMessage(i18n.translate('Watchlists update failed.'), NOTIFICATION.TYPE.ERROR);
                reqParams.errorCallback();
            },
        },
    );

    const uploadImage = useMutation(
        (imageData: {
            fileName: string;
            data: any;
            successCallback: (result: any) => void;
            errorCallback: (message: string) => void;
        }) => api.report.uploadImage(activeWorkspace?.id, activeCaseId, streamId, imageData.data, imageData.fileName),
        {
            onSuccess: (data, imageData) => {
                imageData.successCallback(data);
            },
            onError: (data: any, imageData) => {
                console.log(data);
                imageData.errorCallback(data.message);
            },
        },
    );

    useEffect(() => {
        if (streamId && activeWorkspace?.id && activeCaseId) {
            fetchReport();
        }
    }, [tags, dateRange]);

    return {
        status,
        setStatus,
        report,
        streamId,
        collection,
        getPostById,
        setStreamId,
        initializeStatuses,
        initializeFilters,
        initReport,
        connectionsFiltersPanelRef,
        setConnectionsFiltersPanelRef,
        exportPDF,
        exportJSON,
        dateRange,
        setDateRange,
        tags,
        setTags,
        exportFilters,
        setExportFilters,
        getInitialExportFilters,
        mediaSort,
        setMediaSort,
        mediaDirection,
        setMediaDirection,
        mediaOutputFilters,
        setMediaOutputFilters,
        connectionsSort,
        setConnectionsSort,
        connectionsDirection,
        setConnectionsDirection,
        connectionsOutputFilters,
        setConnectionsOutputFilters,
        postsSort,
        setPostsSort,
        postsDirection,
        setPostsDirection,
        postsOutputFilters,
        setPostsOutputFilters,
        updateMoreResults: updateMoreResults.mutate,
        refreshWatchLists: refreshWatchLists.mutate,
        moreResults,
        uploadImage: uploadImage.mutate,
        find,
        setFind,
        findLinks,
        currentFindLink,
        setCurrentFindLink,
        isConnectionsLoading,
        isMediaLoading,
        isPostsLoading,
        displayHighlights,
        setDisplayHighlights,
        highlightsLinks,
        currentHighlightsLink,
        setCurrentHighlightsLink,
        currentHighlightsWatchlist,
        setCurrentHighlightsWatchlist,
        displayHighlightsRef,
        allHighlights,
        setAllHighlights,
    };
}
